import { Grid } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { ToolboxForm } from 'shared/components/common/toolbox-form';
import { ProjectForm } from 'shared/components/project-forms/components/project-form';
import { Header, Page } from 'redesign';
import { useAlert } from 'shared/components/alerts';
import { HQProjectFormTypeCreate } from 'shared/components/project-forms/types/project-form.types';
import { DetailsForWorkTemplate } from 'shared/components/work-forms/details/work-form-details.types';
import { DateTime } from 'luxon';
import { invariant } from 'shared/utils/utils';
import { useExternalParams } from 'shared/hooks';
import { stringifyFilters } from 'shared/utils/stringify-filters';
import { useCompanyRelativePath } from '../../../../../../../hooks';
import { paths } from '../../../../../../../paths';
import { routes } from '../../../../../../../Routes';
import { useDuplicateProject } from '../../../hooks/use-duplicate-project';
import { OgpProjectDuplicatePayload } from '../../../../../../../services/project-service.types';
import { PROJECT_PLANNING_INITIAL_VALUES } from '../../../filters/hooks/use-project-planning-filters';

type ProjectDuplicateProps = {
  ancestorProjectId: string;
  defaultValues: Partial<HQProjectFormTypeCreate>;
  successMessage: string;
  pageTitle: string;
  workDetails: DetailsForWorkTemplate;
};

export const ProjectDuplicateFormPage = ({
  ancestorProjectId,
  defaultValues,
  successMessage,
  pageTitle,
  workDetails,
}: ProjectDuplicateProps) => {
  const { alertSuccess, alertError } = useAlert();
  const formMethods = useForm<HQProjectFormTypeCreate>({
    defaultValues,
  });

  const navigate = useNavigate();
  const [params] = useExternalParams(PROJECT_PLANNING_INITIAL_VALUES);
  const formattedParams = stringifyFilters({
    ...params,
    toDate: params.toDate
      ? params.toDate.toFormat('yyyy-MM-dd')
      : DateTime.now().toFormat('yyyy-MM-dd'),
  });

  const generatePath = useCompanyRelativePath();
  const mutation = useDuplicateProject();

  const handleSubmit = () => {
    formMethods.handleSubmit((data: HQProjectFormTypeCreate) => {
      invariant(data.location, 'Location is required');

      const payload: OgpProjectDuplicatePayload = {
        departmentId: data.departmentId,
        ancestorProjectId,
        startDate: DateTime.fromFormat(data.startDate, 'yyyy-MM-dd').toISO(),
        endDate: DateTime.fromFormat(data.endDate, 'yyyy-MM-dd').toISO(),
        weekHourVolume: +data.weeklyHours,
        freelanceProperties: data.isFreelance
          ? {
              hourlyRateCents: Math.round(+data.hourlyRate * 100),
              ortApplicable: data.isFreelanceOrtApplicable,
            }
          : undefined,
        location: data.location,
        tempWorkProperties: data.isTempwork
          ? {
              ortApplicable: data.isTempworkOrtApplicable,
            }
          : undefined,
      };
      mutation.mutate(payload, {
        onSuccess: (id) => {
          alertSuccess(successMessage);
          navigate(generatePath(routes.Projects, {}, { id, ...formattedParams }));
        },
        onError: (e) => alertError(e),
      });
    })();
  };
  const redirectPath = generatePath(paths.Projects, {}, formattedParams);

  return (
    <FormProvider {...formMethods}>
      <Page
        header={<Header titleText={pageTitle} />}
        toolbox={
          <ToolboxForm
            onSubmit={handleSubmit}
            mutationStatus={mutation.status}
            mode="create"
            onCancel={() => navigate(redirectPath, { replace: true })}
          />
        }
      >
        <Grid container>
          <Grid item xs={12}>
            <ProjectForm
              mode="duplicate"
              isProjectWithActivePlacement={false}
              workDetails={workDetails}
            />
          </Grid>
        </Grid>
      </Page>
    </FormProvider>
  );
};
